/**
 * Currency utility functions
 */

/**
 * Converts currency code to symbol
 * @param currencyCode - ISO currency code (e.g., "USD", "EUR") or full format (e.g., "USD - US Dollar")
 * @returns Currency symbol (e.g., "$", "€")
 */
export const getCurrencySymbol = (currencyCode?: string): string => {
  const symbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    JPY: "¥",
    INR: "₹",
    AUD: "$",
    CAD: "$",
    SGD: "$",
    AED: "د.إ",
    CHF: "CHF",
    CNY: "¥",
    BRL: "R$",
    MXN: "MEX$",
  };

  // Map full currency names (NetSuite refName) to symbols
  const nameToSymbol: Record<string, string> = {
    "us dollar": "$",
    "euro": "€",
    "british pound": "£",
    "japanese yen": "¥",
    "indian rupee": "₹",
    "australian dollar": "$",
    "canadian dollar": "$",
    "singapore dollar": "$",
    "uae dirham": "د.إ",
    "united arab emirates dirham": "د.إ",
    "chinese yuan": "¥",
    "swiss franc": "CHF",
    "brazilian real": "R$",
    "mexican peso": "MEX$",
  };

  if (!currencyCode) return "$";

  // Try ISO code first (e.g. "USD" or "USD - US Dollar")
  const code = currencyCode.split(" ")[0].toUpperCase();
  if (symbols[code]) return symbols[code];

  // Try full currency name match (case-insensitive)
  const lower = currencyCode.trim().toLowerCase();
  if (nameToSymbol[lower]) return nameToSymbol[lower];

  // Partial match — e.g. "British pound sterling"
  const partialMatch = Object.entries(nameToSymbol).find(([name]) => lower.includes(name) || name.includes(lower));
  if (partialMatch) return partialMatch[1];

  return code || "$";
};

// Same full-name keys as getCurrencySymbol's nameToSymbol map, mapped to ISO codes instead of
// symbols. Kept separate (rather than deriving one from the other) since new entries need both.
const nameToIsoCode: Record<string, string> = {
  "us dollar": "USD",
  "euro": "EUR",
  "british pound": "GBP",
  "japanese yen": "JPY",
  "indian rupee": "INR",
  "australian dollar": "AUD",
  "canadian dollar": "CAD",
  "singapore dollar": "SGD",
  "uae dirham": "AED",
  "united arab emirates dirham": "AED",
  "chinese yuan": "CNY",
  "swiss franc": "CHF",
  "brazilian real": "BRL",
  "mexican peso": "MXN",
};

/**
 * Resolve an arbitrary currency string — a bare ISO code ("USD"), a "CODE - Name" string
 * ("USD - US Dollar"), or a full display name as returned by NetSuite's BUILTIN.DF(t.currency)
 * (e.g. "US Dollar", "Euro") — to a 3-letter ISO 4217 code suitable for Intl.NumberFormat.
 * Returns null when it can't confidently resolve one, so callers can fall back appropriately
 * instead of feeding Intl.NumberFormat an invalid code (which throws).
 */
export const resolveCurrencyCode = (currencyString?: string | null): string | null => {
  if (!currencyString) return null;
  const trimmed = currencyString.trim();
  if (!trimmed) return null;

  // "USD" or the code portion of "USD - US Dollar".
  const firstToken = trimmed.split(/[\s-]/)[0].toUpperCase();
  if (/^[A-Z]{3}$/.test(firstToken)) return firstToken;

  // Full display name (NetSuite refName), case-insensitive, exact or partial match.
  const lower = trimmed.toLowerCase();
  if (nameToIsoCode[lower]) return nameToIsoCode[lower];
  const partialMatch = Object.entries(nameToIsoCode).find(([name]) => lower.includes(name) || name.includes(lower));
  if (partialMatch) return partialMatch[1];

  return null;
};
